// B-154 — the reversals of a closing's office, in its detail: each the bank's form CN-FT-045 recorded
// on the day it touched, with the signed form attached. The operation chose this over keeping a
// consolidated workbook (the dashboard in Reportes counts them: `TransaccionesReversas.jsx`).
//
// "Registrar reversa" opens the form's fields in a dialog — the transaction, its value, the cause and
// whom it is attributed to, the account or reference, why it happened, the authorization email — and
// the signed form (PDF, photo or Excel). Recording and withdrawing need the permission to attend the
// office's alerts (`puedeCambiar`); a withdrawn reversal leaves every count. Nothing is drawn for a
// client that records no reversals (`enabled: false`).
//
// B-159 — the reversals the bank's file says (ESTADO = R) arrive on the closing by themselves, with the
// office, date, transaction and value the file gives ("del archivo del banco"). They wait first in the
// list, "Por completar", until a person fills the form's half from the signed CN-FT-045 — cashier,
// motive, attribution — with "Completar" (`completeCierreReversal`); the bank's figures are shown, never
// edited.
//
// B-160 — "Leer formato" sends the signed form (PDF or photo) to `readCierreReversalForm`, which reads it
// and saves nothing: the fields it read fill the form (`camposDesdeFormato`) for the person to check, the
// checkbox gives the attribution, and what disagrees with the bank's row or the form's 3-business-day
// rule is said above the fields. The motive stays the person's choice.
//
// B-164 — a signed form that reached the office as a file (its email, a forward, an upload) is read and
// attached to its reversal by the backend on its own; the reversal then says "formato recibido por
// correo". A form that found none yet — it often arrives before the bank's file — or several, is listed
// under "Formatos recibidos sin reversa", with "Asociar" to one of the closing's reversals that have no
// form, or "Registrar con este formato" (the form prefilled, its file the reversal's).
import { useCallback, useEffect, useState } from "react";
import { FileText, Pencil, Plus, ScanText, Undo2 } from "lucide-react";
import {
  Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Spinner,
} from "@mantis-sas/ui";

import {
  attachCierreReversalForm, completeCierreReversal, createCierreReversal, listCierreReversals, readCierreReversalForm,
  withdrawCierreReversal,
} from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import { fechaDMY, montoLegible } from "../../../utils/cierrePanel";
import { valorParaEnviar } from "../../../utils/cierreTransacciones";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const CAMPO = "mt-1 block w-full rounded-[6px] border bg-card px-2 py-1.5 text-[12.5px]";
const ETIQUETA = "block text-[12px] text-brinks-foreground";

const VACIO = {
  transactionDate: "", cashier: "", transactionType: "", motive: "", attribution: "", amount: "",
  authorizationEmail: true, accountNumber: "", reference: "", explanation: "",
};

/** What still keeps the form from being sent, in words; empty when it can go. */
export function faltantesDeReversa(campos) {
  const faltan = [];
  if (!campos.transactionType) faltan.push("el tipo de transacción");
  if (!valorParaEnviar(campos.amount)) faltan.push("el valor");
  if (!campos.motive) faltan.push("el motivo");
  if (!campos.attribution) faltan.push("la atribución");
  return faltan;
}

/** A form the reader can read: a PDF or a photo, never the bank's Excel. */
export const esFormatoLegible = (archivo) => Boolean(archivo) && /\.(pdf|jpe?g|png)$/i.test(archivo.name || "");

/** "500000.00" as a person types it in the Valor field: "500.000". */
const montoParaCampo = (monto) => new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(Number(monto));

/**
 * B-160 — the form's fields over what the dialog holds: only what the reader found, never a blank over
 * something typed. Completing a bank reversal takes only the form's half; recording a new one also its
 * date, transaction and value. The motive is never filled: it waits for the concordance table.
 */
export function camposDesdeFormato(campos, formato, { completando = false } = {}) {
  if (!formato) return campos;
  const tomar = (valor, actual) => (valor === null || valor === undefined || valor === "" ? actual : valor);
  const siguiente = {
    ...campos,
    cashier: tomar(formato.cashier, campos.cashier),
    attribution: tomar(formato.attribution, campos.attribution),
    accountNumber: tomar(formato.accountNumber, campos.accountNumber),
    reference: tomar(formato.reference, campos.reference),
    explanation: tomar(formato.explanation, campos.explanation),
  };
  if (!completando) {
    siguiente.transactionDate = tomar(formato.transactionDate, campos.transactionDate);
    siguiente.transactionType = tomar(formato.concept, campos.transactionType);
    siguiente.amount = formato.amount ? montoParaCampo(formato.amount) : campos.amount;
  }
  return siguiente;
}

/** What still keeps a bank reversal from being completed: only the form's half. */
export function faltantesDeCompletar(campos) {
  const faltan = [];
  if (!campos.motive) faltan.push("el motivo");
  if (!campos.attribution) faltan.push("la atribución");
  return faltan;
}

/** The line under a reversal, in words: what is known of it, and where it came from. */
export function detalleDeReversa(r) {
  return [
    fechaDMY(r.transactionDate),
    r.motiveLabel,
    r.attributionLabel ? `atribuida a ${r.attributionLabel}` : null,
    r.cashier ? `cajero ${r.cashier}` : null,
    r.origin === "BANCO" ? "del archivo del banco" : `por ${r.recordedBy}`,
    r.completedBy ? `completada por ${r.completedBy}` : null,
    r.formVia ? `formato recibido ${r.formVia === "panel" ? "en el panel" : "por correo"}${r.formReceivedOn ? ` el ${fechaDMY(r.formReceivedOn)}` : ""}` : null,
  ].filter(Boolean).join(" · ");
}

/** B-164 — why a form reached no reversal by itself, in words. */
export const ESTADO_DE_FORMATO = {
  WAITING: "Esperando su reversa en el archivo del banco",
  AMBIGUOUS: "Coincide con varias reversas",
  UNREADABLE: "No se pudo leer",
};

/** The closing's reversals a waiting form may be associated with: live, without a form of their own. */
export const reversasSinFormato = (reversas) => (reversas || []).filter((r) => !r.formVia);

export function ReversasOficina({ token, mantisBusinessId, closingId, fechaCierre, puedeCambiar }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const [campos, setCampos] = useState(VACIO);
  const [archivo, setArchivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [retirando, setRetirando] = useState(null);
  // B-159 — the bank reversal being completed; null while recording a new one.
  const [completando, setCompletando] = useState(null);
  // B-160 — reading the attached form, and what it found to check.
  const [leyendo, setLeyendo] = useState(false);
  const [lectura, setLectura] = useState(null);
  // B-164 — the waiting form a new reversal is recorded from, and the reversal each one would go to.
  const [desdeFormato, setDesdeFormato] = useState(null);
  const [destinoDeFormato, setDestinoDeFormato] = useState({});

  const cargar = useCallback(async () => {
    try {
      const res = await listCierreReversals(token, mantisBusinessId, closingId);
      if (res?.success) setDatos(res.data);
      else setDatos(null);
    } catch (e) {
      setDatos(null);
    }
  }, [token, mantisBusinessId, closingId]);

  useEffect(() => { if (closingId) cargar(); }, [closingId, cargar]);

  if (!datos || !datos.enabled) return null;

  const abrir = () => {
    setCompletando(null);
    setDesdeFormato(null);
    setLectura(null);
    setCampos({ ...VACIO, transactionDate: fechaCierre || "" });
    setArchivo(null);
    setError(null);
    setAbierto(true);
  };
  const abrirCompletar = (r) => {
    setCompletando(r);
    setDesdeFormato(null);
    setLectura(null);
    setCampos({
      ...VACIO,
      cashier: r.cashier || "",
      motive: r.motive || "",
      attribution: r.attribution || "",
      authorizationEmail: r.authorizationEmail !== false,
      accountNumber: r.accountNumber || "",
      reference: r.reference || "",
      explanation: r.explanation || "",
    });
    setArchivo(null);
    setError(null);
    setAbierto(true);
  };
  const registrarConFormato = (formato) => {
    setCompletando(null);
    setDesdeFormato(formato);
    setLectura(null);
    setCampos(camposDesdeFormato({ ...VACIO, transactionDate: fechaCierre || "" }, formato.form));
    setArchivo(null);
    setError(null);
    setAbierto(true);
  };

  const asociar = async (formato) => {
    const reversalId = destinoDeFormato[formato.id];
    if (!reversalId) return;
    setEnviando(true);
    setError(null);
    try {
      const res = await attachCierreReversalForm(token, mantisBusinessId, formato.id, reversalId);
      if (res?.success) await cargar();
      else setError(correctionError(res));
    } catch (e) {
      setError("No se pudo asociar el formato.");
    }
    setEnviando(false);
  };

  const cambiar = (clave) => (e) => setCampos({ ...campos, [clave]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const faltan = completando ? faltantesDeCompletar(campos) : faltantesDeReversa(campos);

  const guardar = async () => {
    setEnviando(true);
    setError(null);
    try {
      const res = completando
        ? await completeCierreReversal(token, mantisBusinessId, completando.id, {
          cashier: campos.cashier,
          motive: campos.motive,
          attribution: campos.attribution,
          authorizationEmail: campos.authorizationEmail,
          accountNumber: campos.accountNumber,
          reference: campos.reference,
          explanation: campos.explanation,
        }, archivo)
        : await createCierreReversal(token, mantisBusinessId, closingId, {
          ...campos, amount: valorParaEnviar(campos.amount), formId: desdeFormato ? desdeFormato.id : undefined,
        }, archivo);
      if (res?.success) {
        setAbierto(false);
        await cargar();
      } else {
        setError(correctionError(res));
      }
    } catch (e) {
      setError(completando ? "No se pudo completar la reversa." : "No se pudo registrar la reversa.");
    }
    setEnviando(false);
  };

  const leerFormato = async () => {
    setLeyendo(true);
    setError(null);
    try {
      const destino = completando ? { id: completando.id } : { closingId };
      const res = await readCierreReversalForm(token, mantisBusinessId, destino, archivo);
      if (res?.success) {
        setCampos((actuales) => camposDesdeFormato(actuales, res.data.form, { completando: Boolean(completando) }));
        setLectura({
          avisos: (res.data.checks || []).filter((c) => !c.ok).map((c) => c.message),
          notas: res.data.form?.notes || "",
        });
      } else {
        setError(correctionError(res));
      }
    } catch (e) {
      setError("No se pudo leer el formato.");
    }
    setLeyendo(false);
  };

  const retirar = async (id) => {
    setEnviando(true);
    try {
      const res = await withdrawCierreReversal(token, mantisBusinessId, id);
      if (res?.success) await cargar();
      else setError(correctionError(res));
    } catch (e) {
      setError("No se pudo retirar la reversa.");
    }
    setRetirando(null);
    setEnviando(false);
  };

  const vocabulario = datos.vocabulary || { transactionTypes: [], motives: [], attributions: [] };

  return (
    <section {...guia("Reversas", "Las reversas de la oficina que vienen del archivo del banco, con su formato CN-FT-045. Registra o completa una reversa y adjunta el formato.")} className="overflow-hidden rounded-[12px] border border-brinks-border-strong bg-card shadow-brinks-card">
      <div className="flex items-center justify-between border-b bg-brinks-surface px-3.5 py-2.5">
        <div>
          <h3 className="text-[12px] font-semibold text-brinks-foreground">Reversas</h3>
          <p className="mt-0.5 text-[10.5px] text-brinks-muted-foreground">
            Transacciones que el banco reversó, con su formato de solicitud (CN-FT-045)
          </p>
        </div>
        {puedeCambiar ? (
          <Button variant="outline" size="sm" className="h-7 gap-1" onClick={abrir}>
            <Plus className="h-3.5 w-3.5" /> Registrar reversa
          </Button>
        ) : null}
      </div>

      {error && !abierto ? <p role="alert" className="px-3.5 pt-2 text-[12px] text-brinks-warning">{error}</p> : null}

      {datos.reversals.length ? (
        <ul className="divide-y divide-brinks-border px-3.5">
          {datos.reversals.map((r) => (
            <li key={r.id} className="py-2 text-[12px]">
              <div className="flex items-start justify-between gap-3">
                <span className="min-w-0">
                  <span className="flex flex-wrap items-center gap-1.5 font-medium">
                    {r.transactionTypeLabel} · {montoLegible(r.amount)}
                    {r.pending ? (
                      <span className="rounded-full border border-brinks-warning px-1.5 text-[10px] font-normal text-brinks-warning">Por completar</span>
                    ) : null}
                  </span>
                  <span className="block text-[10.5px] text-brinks-muted-foreground">{detalleDeReversa(r)}</span>
                  {r.explanation ? <span className="block text-[11px]">{r.explanation}</span> : null}
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  {r.documentUrl ? (
                    <a href={r.documentUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-[6px] border px-2 py-1 text-[11px] hover:bg-brinks-surface">
                      <FileText className="h-3.5 w-3.5" /> Formato
                    </a>
                  ) : null}
                  {puedeCambiar && r.origin === "BANCO" && retirando !== r.id ? (
                    <Button
                      size="sm"
                      variant={r.pending ? "outline" : "ghost"}
                      className="h-7 gap-1"
                      onClick={() => abrirCompletar(r)}
                      aria-label={r.pending ? "Completar reversa" : "Editar reversa"}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      {r.pending ? "Completar" : null}
                    </Button>
                  ) : null}
                  {puedeCambiar ? (
                    retirando === r.id ? (
                      <>
                        <Button size="sm" variant="outline" className="h-7" disabled={enviando} onClick={() => retirar(r.id)}>Retirar</Button>
                        <Button size="sm" variant="ghost" className="h-7" disabled={enviando} onClick={() => setRetirando(null)}>No</Button>
                      </>
                    ) : (
                      <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => setRetirando(r.id)} aria-label="Retirar reversa">
                        <Undo2 className="h-3.5 w-3.5" />
                      </Button>
                    )
                  ) : null}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-3.5 py-3 text-[12px] text-brinks-muted-foreground">Sin reversas registradas en este cierre</p>
      )}

      {(datos.forms || []).length ? (
        <div className="border-t px-3.5 py-2.5">
          <h4 className="text-[11.5px] font-semibold text-brinks-foreground">Formatos recibidos sin reversa</h4>
          <p className="text-[10.5px] text-brinks-muted-foreground">
            Llegaron con los archivos de la oficina y no encontraron solos su reversa.
          </p>
          <ul className="mt-1 divide-y divide-brinks-border">
            {datos.forms.map((f) => {
              const opciones = reversasSinFormato(datos.reversals);
              return (
                <li key={f.id} className="py-2 text-[12px]">
                  <span className="block font-medium">
                    {f.form?.amount ? montoLegible(f.form.amount) : "Valor sin leer"}
                    {f.form?.cashier ? ` · ${f.form.cashier}` : ""}
                  </span>
                  <span className="block text-[10.5px] text-brinks-muted-foreground">
                    {[ESTADO_DE_FORMATO[f.status] || f.status, `recibido el ${fechaDMY(f.receivedOn)}`, f.sender, f.documentFilename]
                      .filter(Boolean).join(" · ")}
                  </span>
                  <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {f.documentUrl ? (
                      <a href={f.documentUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-[6px] border px-2 py-1 text-[11px] hover:bg-brinks-surface">
                        <FileText className="h-3.5 w-3.5" /> Formato
                      </a>
                    ) : null}
                    {puedeCambiar && opciones.length ? (
                      <>
                        <select
                          aria-label="Reversa del formato"
                          className="h-7 rounded-[6px] border bg-card px-1.5 text-[11.5px]"
                          value={destinoDeFormato[f.id] || ""}
                          onChange={(e) => setDestinoDeFormato({ ...destinoDeFormato, [f.id]: e.target.value })}
                        >
                          <option value="">Elige la reversa…</option>
                          {opciones.map((r) => (
                            <option key={r.id} value={r.id}>{`${r.transactionTypeLabel} · ${montoLegible(r.amount)} · ${fechaDMY(r.transactionDate)}`}</option>
                          ))}
                        </select>
                        <Button size="sm" variant="outline" className="h-7" disabled={!destinoDeFormato[f.id] || enviando} onClick={() => asociar(f)}>
                          Asociar
                        </Button>
                      </>
                    ) : null}
                    {puedeCambiar ? (
                      <Button size="sm" variant="ghost" className="h-7" disabled={enviando} onClick={() => registrarConFormato(f)}>
                        Registrar con este formato
                      </Button>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      <Dialog open={abierto} onOpenChange={(v) => (!enviando ? setAbierto(v) : null)}>
        <DialogContent className="brinks-scope max-h-[90vh] overflow-y-auto font-redhat sm:max-w-[640px]">
          <DialogHeader>
            <div className="flex min-w-0 items-center gap-1">
              <DialogTitle>{completando ? "Completar reversa" : "Registrar reversa"}</DialogTitle>
              <BotonGuia guiaId="dialogo-reversa" titulo="Reversa" texto="Registra una reversa con los datos del formato de solicitud de reverso CN-FT-045 y el formato firmado, o completa una que llegó en el archivo del banco." nombre="Reversa" />
            </div>
            <DialogDescription>
              {completando
                ? "Lo que dice el formato de solicitud de reverso (CN-FT-045) firmado. La fecha, la transacción y el valor son los del archivo del banco."
                : "Los datos del formato de solicitud de reverso (CN-FT-045) y el formato firmado."}
            </DialogDescription>
          </DialogHeader>
          {completando ? (
            <dl {...guia("Del archivo del banco", "La fecha, la transacción y el valor vienen del archivo del banco y no se cambian aquí.")} className="grid grid-cols-3 gap-3 rounded-[8px] border bg-brinks-surface px-3 py-2 text-[12px]">
              <div><dt className="text-[10.5px] text-brinks-muted-foreground">Fecha</dt><dd>{fechaDMY(completando.transactionDate)}</dd></div>
              <div><dt className="text-[10.5px] text-brinks-muted-foreground">Transacción</dt><dd>{completando.transactionLabel || completando.transactionTypeLabel}</dd></div>
              <div><dt className="text-[10.5px] text-brinks-muted-foreground">Valor</dt><dd>{montoLegible(completando.amount)}</dd></div>
            </dl>
          ) : null}
          <div {...guia("Formato firmado", "El CN-FT-045 firmado, en PDF, foto o Excel. Se guarda en el cierre de la oficina.")} className="rounded-[8px] border px-3 py-2">
            <label className={ETIQUETA}>
              Formato firmado (PDF, foto o Excel)
              <input
                type="file"
                aria-label="Formato firmado"
                accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                className="mt-1 block w-full text-[12px]"
                onChange={(e) => {
                  setArchivo(e.target.files && e.target.files[0] ? e.target.files[0] : null);
                  setLectura(null);
                }}
              />
            </label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" className="h-7 gap-1" disabled={!esFormatoLegible(archivo) || leyendo || enviando} onClick={leerFormato}>
                {leyendo ? <Spinner className="h-3.5 w-3.5" /> : <ScanText className="h-3.5 w-3.5" />}
                Leer formato
              </Button>
              <span className="text-[11px] text-brinks-muted-foreground">
                {leyendo ? "Leyendo el formato…" : "Llena los campos con lo que dice el formato; revísalos antes de guardar."}
              </span>
            </div>
            {lectura ? (
              <div className="mt-2 space-y-1 text-[11.5px]" aria-label="Lectura del formato">
                {lectura.avisos.length ? lectura.avisos.map((aviso) => (
                  <p key={aviso} className="text-brinks-warning">{aviso}</p>
                )) : <p className="text-brinks-success">El formato coincide con el archivo del banco.</p>}
                {lectura.notas ? <p className="text-brinks-muted-foreground">{lectura.notas}</p> : null}
                <p className="text-brinks-muted-foreground">El motivo lo eliges tú.</p>
              </div>
            ) : null}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {!completando ? (
              <label className={ETIQUETA}>
                Fecha de la transacción
                <input type="date" aria-label="Fecha de la transacción" className={CAMPO} value={campos.transactionDate} onChange={cambiar("transactionDate")} />
              </label>
            ) : null}
            <label className={ETIQUETA}>
              Cajero (corresponsal)
              <input aria-label="Cajero" className={CAMPO} value={campos.cashier} onChange={cambiar("cashier")} maxLength={500} />
            </label>
            {!completando ? (
              <>
                <label className={ETIQUETA}>
                  Tipo de transacción
                  <select aria-label="Tipo de transacción" className={CAMPO} value={campos.transactionType} onChange={cambiar("transactionType")}>
                    <option value="">Elige…</option>
                    {vocabulario.transactionTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </label>
                <label className={ETIQUETA}>
                  Valor
                  <input aria-label="Valor" inputMode="decimal" className={CAMPO} value={campos.amount} onChange={cambiar("amount")} placeholder="1.500.000" />
                </label>
              </>
            ) : null}
            <label className={ETIQUETA}>
              Motivo de la reversa
              <select {...guia("Motivo", "El motivo de la reversa, como aparece en el formato.")} aria-label="Motivo" className={CAMPO} value={campos.motive} onChange={cambiar("motive")}>
                <option value="">Elige…</option>
                {vocabulario.motives.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </label>
            <label className={ETIQUETA}>
              Atribución
              <select {...guia("Atribución", "A quién se atribuye el error: la oficina, el cliente u otro.")} aria-label="Atribución" className={CAMPO} value={campos.attribution} onChange={cambiar("attribution")}>
                <option value="">Elige…</option>
                {vocabulario.attributions.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </label>
            <label className={ETIQUETA}>
              Número de cuenta del cliente
              <input aria-label="Número de cuenta" className={CAMPO} value={campos.accountNumber} onChange={cambiar("accountNumber")} maxLength={500} />
            </label>
            <label className={ETIQUETA}>
              Referencia del convenio o número de crédito
              <input aria-label="Referencia" className={CAMPO} value={campos.reference} onChange={cambiar("reference")} maxLength={500} />
            </label>
            <label className={`${ETIQUETA} sm:col-span-2`}>
              Causa del error
              <textarea {...guia("Causa del error", "Qué pasó, en tus palabras o como lo dice el formato.")} aria-label="Causa del error" className={CAMPO} rows={3} value={campos.explanation} onChange={cambiar("explanation")} maxLength={500} />
            </label>
            <label className="flex items-center gap-2 text-[12px]">
              <input type="checkbox" checked={campos.authorizationEmail} onChange={cambiar("authorizationEmail")} />
              Tiene correo de autorización
            </label>
          </div>
          {error ? <p role="alert" className="text-[12px] text-brinks-danger">{error}</p> : null}
          {faltan.length ? <p className="text-[11.5px] text-brinks-muted-foreground">Falta {faltan.join(", ")}.</p> : null}
          <DialogFooter>
            <Button variant="outline" disabled={enviando} onClick={() => setAbierto(false)}>Cancelar</Button>
            <Button {...guia("Guardar", "Guarda la reversa. Debajo se dice qué falta mientras no esté completa.")} disabled={faltan.length > 0 || enviando} onClick={guardar} className="gap-1.5">
              {enviando ? <Spinner className="h-3.5 w-3.5" /> : null}
              {completando ? "Guardar" : "Registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
